/** REST client for /api/auth/* and friends. Cookie-authenticated. */
import { statusMessage } from './http-status.js';
import { useSyncExternalStore } from 'react';
import type { PublicUser, SessionInfo } from '@home/shared';
import { clearCache } from './snapshot-cache.js';
import { getPref, setPref } from './prefs.js';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** Server error code, e.g. "invalid_credentials", "must_change_password". */
    readonly code?: string,
  ) {
    super(message);
  }
}

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers: body !== undefined ? { 'content-type': 'application/json' } : {},
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch {
    throw new ApiError("Can't reach the server. Check your connection and try again.", 0);
  }
  let data: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    // Server errors are { error: code, message? }.
    const body = data && typeof data === 'object' ? (data as { error?: unknown; message?: unknown }) : {};
    const code = typeof body.error === 'string' ? body.error : undefined;
    const msg =
      typeof body.message === 'string' ? body.message : statusMessage(res.status);
    throw new ApiError(msg, res.status, code);
  }
  return data as T;
}

const userOf = (d: unknown): PublicUser =>
  d && typeof d === 'object' && 'user' in d ? (d as { user: PublicUser }).user : (d as PublicUser);

export const api = {
  me: async () => userOf(await req<unknown>('GET', '/api/auth/me')),
  login: async (username: string, password: string) =>
    userOf(await req<unknown>('POST', '/api/auth/login', { username, password })),
  logout: async () => {
    forgetUser();
    try {
      return await req<unknown>('POST', '/api/auth/logout');
    } finally {
      await clearCache();
    }
  },
  redeem: async (code: string) => userOf(await req<unknown>('POST', '/api/auth/redeem', { code })),
  changePassword: (current: string, next: string) => req<unknown>('POST', '/api/auth/password', { current, next }),
  sessions: async () => {
    const d = await req<unknown>('GET', '/api/me/sessions');
    return (Array.isArray(d) ? d : ((d as { sessions?: SessionInfo[] })?.sessions ?? [])) as SessionInfo[];
  },
  revokeSession: (id: string) => req<unknown>('DELETE', `/api/me/sessions/${encodeURIComponent(id)}`),
  /** Test notification to this user's own registered phones. */
  testPush: (body: { homeId?: string; category?: string } = {}) =>
    req<{ results: Array<{ deviceId: string; homeId: string; status: number; message?: unknown }>; apns: 'live' | 'dry_run' | 'not_configured' }>('POST', '/api/native/test-push', body),
};

// ------------------------------------------------------------------ session

export type AuthState =
  | { status: 'loading' }
  | { status: 'anon' }
  | { status: 'offline' }
  | { status: 'authed'; user: PublicUser };

let auth: AuthState = { status: 'loading' };
const listeners = new Set<() => void>();

export function setAuth(next: AuthState): void {
  auth = next;
  if (next.status === 'authed') rememberUser(next.user);
  listeners.forEach((fn) => fn());
}

export function getAuth(): AuthState {
  return auth;
}

export function useAuth(): AuthState {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => auth,
    () => auth,
  );
}

// The last signed-in user, so a cold start without network can still open the
// app on its cached house state (lib/snapshot-cache.ts) instead of a dead end.
const LAST_USER = 'lastUser';

function rememberUser(u: PublicUser): void {
  setPref(LAST_USER, u.mustChangePassword ? null : JSON.stringify(u));
}

function forgetUser(): void {
  setPref(LAST_USER, null);
}

function cachedUser(): PublicUser | null {
  try {
    const raw = getPref(LAST_USER);
    const u = raw ? (JSON.parse(raw) as PublicUser) : null;
    return u && typeof u.id === 'string' ? u : null;
  } catch {
    return null;
  }
}

export async function loadSession(): Promise<void> {
  try {
    const user = await api.me();
    setAuth({ status: 'authed', user });
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      forgetUser();
      setAuth({ status: 'anon' });
      return;
    }
    // Server unreachable: open on the cached state; the socket keeps retrying
    // and a 4401 on reconnect still routes to sign-in.
    const u = cachedUser();
    setAuth(u ? { status: 'authed', user: u } : { status: 'offline' });
  }
}
